<template>
  <div :class="{ 'shift-top': userRoleGroup !== userRoleGroups.SP }" class="projects projects-view">
    <projects-requests-header v-if="userRoleGroup === userRoleGroups.SP" :inRequestedStatus="requestedRequests" />
    <div class="project-filters-header">
      <advanced-filters-header
        layout='projects'
        @applyDomains='apply'
        @applyOrganizations='() => {}'
      />
      <div class="project-search">
        <v-text-field
          :append-icon="search?'close':'search'"
          @click:append="clearSearch"
          @keydown.native.escape="clearSearch"
          @keyup.enter='submitSearchFilter'
          label="Search"
          solo
          v-model="search"
          class="employees-tab-search"
        ></v-text-field>
      </div>
    </div>
    <div class="project-list-main-content" :class="{ loading: projectListLoading }">
      <template v-if="pageReady">
        <div class='project-list__activities-wrapper' v-if='hasActivities'>
          <div class='project-list__activities-wrapper__title'>
            {{ $t('activity.log.activity.feed.title') }}
            <div class='project-list__activities-wrapper__title__display-toggle' v-if="getActivities.length > 5" :class='{ open: isShowAll}'>
              <span text class='hide-activities-btn' @click.stop="isShowAll = !isShowAll">
                {{ captionOfActivitiesToggleButton }}
              </span>
            </div>
          </div>
          <div class='project-list__activities-wrapper__content' :style="`flex-wrap: ${isShowAll ? 'wrap' : 'nowrap'};`">
            <activity-feed-row v-for="activity in collapsedActivities" :key='activity.id'
              :activity="activity"
              :name="'Import Configuration from CSV'"
              @updateActivity='updateActivity'
              @openActivityPopup='openActivityPopup'
            />
          </div>
        </div>
        <div class='project-list__scans-title'>
          {{ $t('activity.log.scans.title') }}
        </div>
        <div class='project-list-content'>
          <wombat-table
            class="projects-table"
            :items="formattedProjects"
            :columns="tableColumns"
            :resize="false"
            :loadingItems="false"
            :columnsReorder="false"
            :itemHeight="58"
            :noResultsMessage="$t('noResultsFound')"
            @columnSorted="columnSorted"
          >
            <component
              slot="row-cell"
              slot-scope="rowCell"
              class="row-cell"
              :is="tableRowCellComponentsHash[rowCell.column.name] || tableRowCellComponentsHash.default"
              :item="rowCell.item"
              :column="rowCell.column"
              @loadProjects='loadProjects({}, true)'
            />
          </wombat-table>
          <div class="project-list-content__footer">
            <advanced-pagination
              tab='projects'
              :reset-page='resetPage'
              :pages-amount='pages'
              :is-show-pagination='isShowPagination'
              @changedSelectedPage='changedSelectedPage'
              @pagedRested='resetPage = false'
              @changedItemsAmountPerPage='changedItemsAmountPerPage'
            />
          </div>
        </div>
      </template>
      <page-loading-overlay
        :show="!pageReady || projectListLoading"
        :progress="projectsProgress"
        contained
      />
    </div>
    <activity-details-popup
      v-if='activityForPopup'
      :visible-popup='activityPopup'
      :activity='activityForPopup'
      @clicked='closeActivityPopup'
    />
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import _ from 'lodash';

import store from '@/store';
import { views } from '@/router/constants';
import { activityLogColumns, getEmptyColumn } from '@/services/tableColumns';
import WombatTable from '@/components/WombatTable/WombatTable.vue';
import NewProjectHeader from '@/components/NewProjectHeader/NewProjectHeader.vue';
import WombatRowDefaultCell from '@/components/WombatTable/WombatRowDefaultCell.vue';
import ProjectNameTableCell from '@/components/tableCells/ProjectNameTableCell.vue';
import ProjectProviderTableCell from '@/components/tableCells/ProjectProviderTableCell.vue';
import ProjectStatusOrRetryTableCell from '@/components/tableCells/ProjectStatusOrRetryTableCell.vue';
import ProjectProgressTableCell from '@/components/tableCells/ProjectProgressTableCell.vue';
import ProjectCustomerTableCell from '@/components/tableCells/ProjectCustomerTableCell.vue';
import ProjectsRequestsHeader from '@/components/ProjectsRequestsHeader/ProjectsRequestsHeader.vue';
import { UPDATE_ITEMS_PER_PAGE } from '@/store/pagination/actionTypes';
import { userRoleGroups as USER_ROLE_GROUPS } from '@/constants/auth';
import { formatStartDateWithTime } from '@/utils/dateUtils';
import { formatToLocalWithTimeAndYear, formatTestCardDateWithYear, getFormattedDuration } from '@/utils/timezoneUtils';
import { numberOfRequestsWithRequestsStatus } from '@/utils/requestUtils';
import { SET_NEW_PROJECT, SET_NEW_PROJECT_NAVIGATION } from '@/store/newProject/actionTypes';
import {
  emptyProject, initNewProjectNavigation, projectStatuses, TEST_REPEAT_STATUS,
} from '@/constants/projects';
import {
  CHANGE_CLOUD_ACCOUNTS_LIST_OPTIONS,
  CHANGE_PROJECT_LIST_OPTIONS,
  LOAD_DOMAINS_FROM_CONFIGURATION,
  LOAD_PROJECTS,
  SET_LOADING_DATA_FLAG,
} from '@/store/project/actionTypes';
import { CLEAR_PROJECTS } from '@/store/project/mutationTypes';
import { LOAD_DOMAINS } from '@/store/user/actionTypes';
import ProjectIssuesCountTableCell from '@/components/tableCells/ProjectIssuesCountTableCell.vue';
import PageLoadingOverlay from '@/components/common/PageLoadingOverlay.vue';
import AdvancedPagination from '@/components/common/AdvancedPagination.vue';
import moment from 'moment';
import ActivityFeedRow from '@/components/ActivityLog/ActivityFeedRow.vue';
import {
  LOAD_ACTIVITIES,
  LOAD_ACTIVITY_DATA,
  RESET_ACTIVITY_DATA,
  UPDATE_ACTIVITY,
} from '@/store/activity/actionTypes';
import { CLEAR_ACTIVITIES, CLEAR_REPORT_ACTIVITIES } from '@/store/activity/mutationTypes';
import ActivityDetailsPopup from '@/components/popup/ActivityDetailsPopup.vue';
import AdvancedFiltersHeader from '@/components/common/AdvancedFiltersHeader.vue';
import { CLEAR_DOMAIN_FILTER } from '@/store/filters/actionTypes';

// eslint-disable-next-line no-unused-vars
const Status = {
  declined: 0,
  canceled: 1,
  failed: 2,
  'repeat-in-progress': 3,
  'in-progress': 4,
  finished: 5,
};

export default {
  name: 'ProjectsList',
  components: {
    AdvancedFiltersHeader,
    ActivityDetailsPopup,
    ActivityFeedRow,
    AdvancedPagination,
    PageLoadingOverlay,
    WombatTable,
    WombatRowDefaultCell,
    ProjectNameTableCell,
    ProjectProviderTableCell,
    ProjectStatusOrRetryTableCell,
    ProjectProgressTableCell,
    ProjectCustomerTableCell,
    ProjectsRequestsHeader,
    NewProjectHeader,
    ProjectIssuesCountTableCell,
  },
  data() {
    return {
      polling: null,
      pollingProjectsUpdates: null,
      pollingActivityUpdates: null,
      search: '',
      page: 1,
      activityPopup: false,
      activityForPopup: null,
      isShowAll: false,
      resetPage: false,
      pageReady: false,
      activityLogRowsEl: null,
    };
  },
  beforeDestroy() {
    clearInterval(this.pollingProjectsUpdates);
    clearInterval(this.polling);
    clearInterval(this.pollingActivityUpdates);
    this.$store.commit(CLEAR_ACTIVITIES);
    this.$store.commit(CLEAR_REPORT_ACTIVITIES);
    this.$store.commit(CLEAR_PROJECTS);
    if (this.activityLogRowsEl) {
      this.activityLogRowsEl.removeEventListener('scroll', this.onActivityLogRowsScroll);
    }
    window.removeEventListener('resize', this.syncActivityLogHeaderScroll);
  },
  async mounted() {
    this.pageReady = false;
    await this.loadProjects({
      page: 1,
      limit: this.itemsPerPage,
      domain: this.domainToPass,
    }, true);
    await this.$store.dispatch(LOAD_ACTIVITIES);
    await store.dispatch(SET_NEW_PROJECT, _.cloneDeep(emptyProject));
    await store.dispatch(SET_NEW_PROJECT_NAVIGATION, _.cloneDeep(initNewProjectNavigation));
    this.pageReady = true;
    this.$nextTick(this.syncActivityLogHeaderScroll);
    window.addEventListener('resize', this.syncActivityLogHeaderScroll);
    await this.pollData();
    await this.pollActivityData();
  },
  computed: {
    ...mapGetters([
      'activities',
      'activitiesLoading',
      'paginationItemsPerPageByTabs',
      'projectsPaginationState',
      'projectListLoading',
      'projects',
      'projectsListSort',
      'detailedProject',
      'userRoleGroup',
      'requests',
      'domains',
      'configurationDomains',
      'domainFilter',
      'profile',
      'organizationFilter',
      'projectsRefId',
      'projectsProgress',
      'projectsRunningInterval',
      'activityData',
    ]),
    hasActivities() {
      return (this.activities.length > 0);
    },
    getActivities() {
      return this.activities;
    },
    collapsedActivities() {
      return this.isShowAll ? this.getActivities : this.getActivities.slice(0, 5);
    },
    itemsPerPage() {
      return this.paginationItemsPerPageByTabs.projects;
    },
    domainToPass() {
      return this.domainSelected.length > 0 ? this.domainSelected : this.domains;
    },
    listOfDomains() {
      const domainsSet = new Set(this.domains);
      this.configurationDomains.forEach(configurationDomain => domainsSet.add(configurationDomain.configData));
      return Array.from(domainsSet);
    },
    domainSelected: {
      get() {
        return this.domainFilter;
      },
    },
    tableColumns() {
      const columns = [
        activityLogColumns.number,
        activityLogColumns.asset,
        activityLogColumns.activity,
        activityLogColumns.type,
        activityLogColumns.startDate,
        activityLogColumns.endDate,
        activityLogColumns.duration,
        activityLogColumns.progress,
        activityLogColumns.status,
        activityLogColumns.issuesCount,
      ]
        .filter(column => column)
        .map(column => ({
          ...column,
          title: column.title && this.$t(column.title),
          sort: this.projectsListSort[column.sortingField || column.field],
        }));

      return [getEmptyColumn(32), ...columns, getEmptyColumn(32)];
    },
    tableRowCellComponentsHash() {
      return {
        asset: ProjectNameTableCell,
        status: ProjectStatusOrRetryTableCell,
        progress: ProjectProgressTableCell,
        issuesCount: ProjectIssuesCountTableCell,
        default: WombatRowDefaultCell,
      };
    },
    formattedProjects() {
      return this.projects.map(project => {
        let duration = '';
        let newRepetitiveScan = false;
        if (project.scanData?.createdDate) {
          duration = getFormattedDuration(project.endDate, project.scanData.createdDate);
          if (project.endDate) {
            if (project.endDate < project.scanData.createdDate) {
              newRepetitiveScan = true;
            }
          } else {
            newRepetitiveScan = true;
          }
        } else if (project.endDate) {
          duration = getFormattedDuration(project.endDate, `${project.testCard.startDate} ${project.testCard.startTime}`);
        }

        let { endDate } = project;
        if ([projectStatuses.CANCELED, projectStatuses.IN_PROGRESS, projectStatuses.REPEAT_IN_PROGRESS].includes(project.status)) {
          endDate = null;
          duration = null;
        }

        // Format end date with timezone handling
        let formattedEndDate = '';
        if (!newRepetitiveScan && endDate) {
          formattedEndDate = formatToLocalWithTimeAndYear(endDate);
        }

        return {
          ...project,
          issuesCount: project.issuesCount,
          asset: project.testCard.url,
          end: endDate,
          activity: this.$t(`test.testType.${project.testCard.type}`),
          formattedType: this.$t(`test.configurationType.${project.testCard.frequency === TEST_REPEAT_STATUS.never ? 'oneTime' : 'continuous'}`),
          // Use timezone-aware formatting for dates. While PENDING, show the
          // project's own (refreshed) start instead of a previous run's scan-data
          // date — so a retry reads like a fresh scan on the same project. Using
          // project.startDate (not testCard.startDate) keeps the date fresh for
          // repetitive scans too, whose recurring testCard schedule is unchanged.
          formattedStartDate: (project.status !== projectStatuses.PENDING && project.scanData?.createdDate)
            ? formatToLocalWithTimeAndYear(project.scanData.createdDate)
            : formatTestCardDateWithYear(project.startDate || project.testCard.startDate, project.testCard.startTime, project.testCard.timezone),
          formattedEndDate,
          duration: newRepetitiveScan ? null : duration,
        };
      });
    },
    userRoleGroups() {
      return USER_ROLE_GROUPS;
    },
    requestedRequests() {
      return numberOfRequestsWithRequestsStatus(this.requests);
    },
    pages() {
      return this.projectsPaginationState?.pageCount || Math.ceil(this.formattedProjects.length / this.itemsPerPage) || 1;
    },
    isShowPagination() {
      return this.pages > 1;
    },
    captionOfActivitiesToggleButton() {
      return this.isShowAll ? this.$t('infrastructure.technologies.hide.list') : this.$t('infrastructure.technologies.show.all.list');
    },
  },
  watch: {
    formattedProjects() {
      this.$nextTick(this.syncActivityLogHeaderScroll);
    },
    async organizationFilter() {
      await this.$store.dispatch(CLEAR_DOMAIN_FILTER);
    },
    async profile() {
      if (this.profile) {
        await this.$store.dispatch(LOAD_DOMAINS);
        await this.$store.dispatch(LOAD_DOMAINS_FROM_CONFIGURATION);
        await this.$store.dispatch(CHANGE_CLOUD_ACCOUNTS_LIST_OPTIONS, {
          noLimit: true,
        });
      }
    },
    async userRoleGroup() {
      if (this.userRoleGroup && this.userRoleGroup === this.userRoleGroups.CISO) {
        await this.$store.dispatch(LOAD_DOMAINS);
        await this.$store.dispatch(LOAD_DOMAINS_FROM_CONFIGURATION);
        await this.$store.dispatch(CHANGE_CLOUD_ACCOUNTS_LIST_OPTIONS, {
          noLimit: true,
        });
        if (this.pollingProjectsUpdates === null) {
          await this.pollData();
        }
        if (this.pollingActivityUpdates === null) {
          await this.pollActivityData();
        }
      }
    },
  },
  async created() {
    if (this.profile) {
      await this.$store.dispatch(LOAD_DOMAINS);
      await this.$store.dispatch(LOAD_DOMAINS_FROM_CONFIGURATION);
      await this.$store.dispatch(CHANGE_CLOUD_ACCOUNTS_LIST_OPTIONS, {
        noLimit: true,
      });
    }
  },
  methods: {
    clearInterval() {
      clearInterval(this.polling);
      this.polling = null;
    },
    async loadProjects(payload = {}, initiatedByFilterApply = false) {
      this.clearInterval();
      await this.$store.dispatch(LOAD_PROJECTS, {
        domain: this.domainToPass,
        initiatedByFilterApply,
        page: this.page,
        limit: this.itemsPerPage,
        search: this.search,
        ...payload,
      });
      if (this.projectsRefId && this.projectsRefId !== '') {
        this.polling = setInterval(async () => {
          if (!this.projectsRefId || this.projectsRefId === '') {
            this.clearInterval();
          } else if (!this.projectsRunningInterval) {
            await this.$store.dispatch(LOAD_PROJECTS, {
              domain: this.domainToPass,
              initiatedByFilterApply,
              page: this.page,
              limit: this.itemsPerPage,
              search: this.search,
              refId: this.projectsRefId,
              ...payload,
            });
          }
        }, 3000);
      }
    },
    getFormattedDiffBetweenDates(newDate, oldDate) {
      const diffInMinutes = moment(new Date(formatStartDateWithTime(newDate))).diff(moment(new Date(formatStartDateWithTime(oldDate))), 'minutes');
      const diffInMinutesFormatted = diffInMinutes > 9 ? diffInMinutes : `0${diffInMinutes}`;
      if (diffInMinutes && diffInMinutes > 0) {
        if (diffInMinutes / 60 > 1) {
          const remainMinutes = diffInMinutes % 60;
          const remainMinutesFormatted = remainMinutes > 9 ? remainMinutes : `0${remainMinutes}`;
          const diffInHours = Math.floor(diffInMinutes / 60);
          const diffInHoursFormatted = diffInHours > 9 ? diffInHours : `0${diffInHours}`;
          if (diffInHours / 24 > 1) {
            const remainHours = diffInHours % 24;
            const remainHoursFormatted = remainHours > 9 ? remainHours : `0${remainHours}`;
            const diffInDays = Math.floor(diffInHours / 24);
            const diffInDaysFormatted = diffInDays > 9 ? diffInDays : `0${diffInDays}`;
            return `${diffInDaysFormatted}d ${remainHoursFormatted}h ${remainMinutesFormatted}m`;
          }
          return `${diffInHoursFormatted}h ${remainMinutesFormatted}m`;
        }
        return `${diffInMinutesFormatted}m`;
      }
      return '0m';
    },
    async changedItemsAmountPerPage(itemsPerPage) {
      window.scrollTo(0, 0);
      this.page = 1;
      await this.$store.dispatch(UPDATE_ITEMS_PER_PAGE, { itemsPerPage, tab: 'projects' });
      await this.loadProjects({
        domain: this.domainToPass,
        page: this.page,
        limit: itemsPerPage,
        search: this.search,
      }, true);
    },
    async changedSelectedPage(currentPage) {
      window.scrollTo(0, 0);
      this.page = currentPage;
      await this.loadProjects({
        domain: this.domainToPass,
        page: this.page,
        limit: this.itemsPerPage,
        search: this.search,
      }, true);
    },
    async apply() {
      if (this.domainSelected.length > 0) {
        this.pageReady = false;
        await this.$store.dispatch(SET_LOADING_DATA_FLAG, true);
        this.page = 1;
        this.resetPage = true;
        await this.loadProjects({
          page: this.page,
          limit: this.itemsPerPage,
          domain: this.domainToPass,
          search: '',
        }, true);
        await this.$store.dispatch(LOAD_ACTIVITIES);
        this.pageReady = true;
      }
    },
    navigateToNewProject() {
      this.$router.push({ name: views.NEW_PROJECT });
    },
    async updateActivity(payload) {
      await this.$store.dispatch(UPDATE_ACTIVITY, payload);
    },
    async closeActivityPopup() {
      await this.$store.dispatch(RESET_ACTIVITY_DATA);
      this.activityForPopup = null;
      this.activityPopup = false;
    },
    async openActivityPopup(activity) {
      await this.$store.dispatch(LOAD_ACTIVITY_DATA, activity.id);
      this.activityForPopup = this.activityData ? {...this.activityData} : null;
      this.activityPopup = true;
    },
    async columnSorted({ column, direction }) {
      await this.$store.dispatch(CHANGE_PROJECT_LIST_OPTIONS, {
        domain: this.domainToPass,
        sort: direction
          ? {
            [column.sortingField || column.field]: direction,
          }
          : {},
      });
      this.page = 1;
      this.resetPage = true;
      await this.loadProjects({}, true);
    },
    dispatch(e) {
      this.$emit('input', e);
      this.domainSelected = e;
    },
    // On small screens the activity log table scrolls horizontally (see the
    // .wombat-table overrides below) but the header has no scrollbar of its
    // own, so keep it visually aligned with the rows. This lives here rather
    // than in the shared WombatTable component so every other table in the
    // app keeps its current (unaffected) behavior.
    onActivityLogRowsScroll() {
      const outerHeaderEl = this.$el.querySelector('.wombat-table > .wombat-header');
      if (outerHeaderEl && this.activityLogRowsEl) {
        outerHeaderEl.scrollLeft = this.activityLogRowsEl.scrollLeft;
      }
    },
    // The activity log table is wider than the viewport (duration/progress/
    // status need their real size — see the .wombat-table overrides below),
    // so the rows scroll horizontally. The header has no scrollbar of its
    // own, so this keeps it visually aligned with the rows as they scroll.
    // It lives here (not in the shared WombatTable component) so every
    // other table in the app keeps its current, unaffected behavior.
    syncActivityLogHeaderScroll() {
      const rowsEl = this.$el.querySelector('.wombat-table .virtual-list');
      const renderedRow = this.$el.querySelector('.wombat-table .wombat-row');
      // WombatHeader.vue nests a second element carrying the same
      // ".wombat-header" class — that inner one holds the column cells and
      // needs to match a rendered row's width exactly, since flex's
      // max-content sizing isn't reliable with this column mix.
      const innerHeaderEl = this.$el.querySelector('.wombat-table > .wombat-header .wombat-header');
      if (innerHeaderEl && renderedRow) {
        innerHeaderEl.style.width = `${renderedRow.scrollWidth}px`;
      }
      if (!rowsEl || rowsEl === this.activityLogRowsEl) {
        return;
      }
      if (this.activityLogRowsEl) {
        this.activityLogRowsEl.removeEventListener('scroll', this.onActivityLogRowsScroll);
      }
      this.activityLogRowsEl = rowsEl;
      rowsEl.addEventListener('scroll', this.onActivityLogRowsScroll);
    },
    async pollData() {
      // eslint-disable-next-line no-underscore-dangle
      if (!this._isDestroyed) {
        this.pollingProjectsUpdates = setInterval(async () => {
          if (!this.projectsRunningInterval && !this.polling && !this.projectsRefId) {
            await this.loadProjects({
              domain: this.domainToPass,
              page: this.page,
              limit: this.itemsPerPage,
              search: this.search,
              repetitivePolling: true,
            }, false);
          }
        }, 40000);
      }
    },
    async pollActivityData() {
      // eslint-disable-next-line no-underscore-dangle
      if (!this._isDestroyed) {
        this.pollingActivityUpdates = setInterval(async () => {
          await store.dispatch(LOAD_ACTIVITIES);
        }, 40000);
      }
    },
    async clearSearch() {
      this.search = '';
      this.page = 1;
      await this.loadProjects({
        domain: this.domainToPass,
        page: this.page,
        limit: this.itemsPerPage,
        search: this.search,
      }, true);
    },
    async submitSearchFilter() {
      this.page = 1;
      this.resetPage = true;
      await this.loadProjects({
        domain: this.domainToPass,
        page: this.page,
        limit: this.itemsPerPage,
        search: this.search,
      }, true);
    },
  },
};
</script>

<style lang="scss" scoped>
@import "~@/assets/styles/design-system";

.projects-tab-loading {
  align-self: center;
  justify-self: center;
  margin-top: 20%;
}

.projects-view {
  &.shift-top {
    padding-top: $space-6;
  }
}

.project-filters-header {
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  margin-left: $space-8;
  margin-top: $space-2;

  .project-search {
    min-width: rem(400);
    margin-left: $space-8;
    margin-top: 1.8rem; // Align with the input fields (below the labels)
  }
}

.project-list-main-content {
  position: relative;
  min-height: calc(100vh - 140px);

  &.loading {
    > *:not(.page-loading-overlay) {
      opacity: 0.3;
      pointer-events: none;
    }
  }
}

.project-list-content {
  &__footer {
    margin-top: $space-6;
    margin-bottom: $space-6;
  }
}

.project-list {
  &__scans-title {
    padding-left: $space-8;
    padding-top: $space-8;
    padding-bottom: $space-5;
    color: var(--text-primary);
    font-family: $font-family-bold;
    font-size: $font-size-2xl;
    font-weight: bold;
    line-height: $line-height-tight;
    letter-spacing: $letter-spacing-normal;
  }

  &__activities-wrapper {
    &__title {
      padding-left: $space-8;
      padding-top: $space-8;
      padding-bottom: $space-5;
      color: var(--text-primary);
      font-family: $font-family-bold;
      font-size: $font-size-2xl;
      font-weight: bold;
      line-height: $line-height-tight;
      letter-spacing: $letter-spacing-normal;

      &__display-toggle {
        width: rem(100);
        background: transparent;
        text-align: right;
        position: relative;
        left: rem(289);
        margin-top: -$space-5;

        .hide-activities-btn {
          color: var(--brand-primary);
          font-family: $font-family-regular;
          font-size: $font-size-base;
          font-weight: normal;
          letter-spacing: $letter-spacing-normal;

          &:hover {
            cursor: pointer;
          }
        }
      }
    }
  }
}
</style>

<style lang="scss">
@import "~@/assets/styles/design-system";

.projects-view {
  .wombat-table {
    .wombat-header{
      padding-bottom: 10px;
    }
    .cell.column-issuescount{
      flex-grow: 0;
      min-width: 0;
    }
    // The status data cell centers its badge (.project-status-or-retry has
    // justify-content: center), but the header label default-aligns left —
    // center the header label to match so it sits above the badge instead
    // of its left edge. Progress isn't touched: its data (percent + circle)
    // is left-aligned, same as its header already is.
    .header-column.column-status .content {
      display: flex;
      justify-content: center;
    }
    .cell.column-duration {
      color: var(--text-primary);
      opacity: 0.5;
    }
    --displayissuecountcell: none;
    --displaystatuscell: block;
    padding: 15px 0;

    // Whenever the viewport is narrower than this table's natural column
    // width, flex-shrink squeezes columns (duration, progress, status) well
    // below the size their content needs. That makes the progress circle
    // overlap the percentage text and wraps the status badge onto two lines
    // that bleed into the row above. Keep every column at its real width
    // instead, and let the rows scroll horizontally. This is scoped to this
    // table only — the shared WombatTable component is untouched, so every
    // other table in the app keeps its existing (working) behavior.
    .wombat-row {
      width: max-content !important;
      min-width: 100%;
    }

    // The outer ".wombat-header" needs to stay at its normal (viewport)
    // width with the overflow hidden, so it has real scrollable content and
    // syncActivityLogHeaderScroll() (this component's own mounted hook) can
    // drive its scrollLeft in step with the rows. The inner draggable list
    // (WombatHeader.vue nests a second element with the same class) is sized
    // to match a rendered row exactly, in that same JS hook — flex's
    // max-content sizing is unreliable here because of how the columns mix
    // fixed widths with flex-basis fluid ones.
    .wombat-header {
      overflow-x: hidden;

      // The inner element is a flex child of the outer one; without this,
      // flexbox's default flex-shrink silently shrinks the width set by
      // syncActivityLogHeaderScroll() back down to fit, erasing the overflow
      // we need for the scroll-sync to have any effect.
      .wombat-header {
        flex-shrink: 0;
      }
    }

    .virtual-list {
      overflow-x: auto;
    }

    .cell {
      flex-shrink: 0;
    }

    .project-status .status {
      white-space: nowrap;
    }

    // vue-virtual-scroller clips each recycled row to the scroller's width
    // by default, which would hide the overflow we rely on for horizontal
    // scrolling — let the row's real (wider) width show through instead.
    .vue-recycle-scroller__item-wrapper {
      overflow: visible;
    }

    .vue-recycle-scroller__item-view {
      width: max-content !important;
      min-width: 100%;
    }

    .wombat-row {
      border-top: none !important;
      border-left: none !important;
      border-right: none !important;
    }

    .wombat-overlay {
      border-bottom: none;
      &:hover {
        background-color: var(--sidebar-active-bg);
        --display: block;
        --displayissuecountcell: flex;
        --displaystatuscell: none;
      }
    }
  }
}
.project-search{
  .v-text-field.v-text-field--solo:not(.v-text-field--solo-flat) > .v-input__control > .v-input__slot {
    box-shadow: none;
    border: 1px solid var(--border-primary);
    border-radius: 2px;
  }
  .v-input__icon.v-input__icon--append {
    margin-top: 0 !important;

    .v-icon {
      color: var(--text-secondary) !important;
      font-size: rem(24) !important;
    }
  }
  // Purple icon when focused or has value
  .v-input--is-focused .v-input__icon--append .v-icon,
  .v-input--is-dirty .v-input__icon--append .v-icon {
    color: var(--brand-primary) !important;
  }
  .v-text-field.v-text-field--solo .v-input__control {
    min-height: 40px;
  }
}
</style>
